<script setup>
import { reactive, watch, computed } from 'vue';
import { useI18n } from 'vue-i18n';

import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';

const props = defineProps({
  assistant: {
    type: Object,
    default: () => ({}),
  },
});

const { t } = useI18n();

const state = reactive({
  enabled: false,
  criteria: [],
});

const addCriterion = () => {
  state.criteria.push({ text: '', enabled: true });
};

const removeCriterion = index => {
  state.criteria.splice(index, 1);
};

// Returns this section's partial payload only — never spreads
// props.assistant.config, so the parent page can merge all sections'
// payloads together in one PATCH instead of each section overwriting the
// others' unsaved changes with a stale snapshot of assistant.config.
const getPayload = () => {
  const activeCriteria = state.criteria
    .filter(item => item.enabled && item.text.trim())
    .map(item => item.text.trim());

  return {
    config: {
      captain_auto_resolve_mode: state.enabled ? 'evaluated' : 'disabled',
      captain_auto_resolve_criteria: activeCriteria,
    },
  };
};

// Compares serialized payloads (not raw state) so derived/normalized state
// doesn't cause a false positive dirty state.
let initialPayloadSnapshot = JSON.stringify(getPayload());

const updateStateFromAssistant = assistant => {
  const { config = {} } = assistant;
  const mode = config.captain_auto_resolve_mode;
  state.enabled = mode && mode !== 'disabled';
  state.criteria = (config.captain_auto_resolve_criteria || []).map(text => ({
    text,
    enabled: true,
  }));
  initialPayloadSnapshot = JSON.stringify(getPayload());
};

const isDirty = computed(
  () => JSON.stringify(getPayload()) !== initialPayloadSnapshot
);

defineExpose({ getPayload, isDirty });

watch(
  () => props.assistant,
  assistant => {
    if (assistant) updateStateFromAssistant(assistant);
  },
  { immediate: true }
);
</script>

<template>
  <div class="flex flex-col gap-4">
    <div class="flex items-center gap-3">
      <Switch v-model="state.enabled" />
      <span class="text-sm text-n-slate-12">
        {{ t('CAPTAIN.ASSISTANTS.FORM.AUTO_RESOLVE.ENABLED_LABEL') }}
      </span>
    </div>

    <div v-if="state.enabled" class="flex flex-col gap-3">
      <label class="text-sm font-medium text-n-slate-12">
        {{ t('CAPTAIN.ASSISTANTS.FORM.AUTO_RESOLVE.CRITERIA_LABEL') }}
      </label>

      <div
        v-for="(criterion, index) in state.criteria"
        :key="index"
        class="flex items-center gap-2"
      >
        <Switch v-model="criterion.enabled" />
        <Input
          v-model="criterion.text"
          class="flex-1"
          :placeholder="
            t('CAPTAIN.ASSISTANTS.FORM.AUTO_RESOLVE.CRITERIA_PLACEHOLDER')
          "
        />
        <Button
          icon="i-lucide-x"
          variant="ghost"
          color="slate"
          size="sm"
          @click="removeCriterion(index)"
        />
      </div>

      <Button
        icon="i-lucide-plus"
        variant="ghost"
        size="sm"
        class="!w-fit !text-n-brand hover:!text-n-brand"
        :label="t('CAPTAIN.ASSISTANTS.FORM.AUTO_RESOLVE.ADD_CRITERION')"
        @click="addCriterion"
      />

      <p class="text-xs text-n-slate-11 italic">
        {{ t('CAPTAIN.ASSISTANTS.FORM.AUTO_RESOLVE.FOOTNOTE') }}
      </p>
    </div>
  </div>
</template>
